import { useEffect } from "react";
import { useLocation } from "wouter";
import { GamePhase } from "@badorbad/shared";
import { useAuth } from "../stores/auth";
import { useGame, RoomExit } from "../stores/game";
import { Lobby } from "./Lobby";
import { Setup } from "./Setup";
import { Turn } from "./Turn";
import { Reveal } from "./Reveal";
import { Results } from "./Results";
import { t } from "../locale";

/**
 * Phase router. The server's phase decides what a player sees, so there is no
 * client-side navigation between game screens — every transition arrives as an
 * event and re-renders this.
 */
export const Room = () => {
  const [, navigate] = useLocation();
  const loading = useAuth((s) => s.loading);
  const phase = useGame((s) => s.phase);
  const wsReady = useGame((s) => s.wsReady);
  const exit = useGame((s) => s.exit);
  const setExit = useGame((s) => s.setExit);
  const roomCode = useGame((s) => s.roomCode);
  const turn = useGame((s) => s.turn);
  const reveal = useGame((s) => s.reveal);
  const ranking = useGame((s) => s.ranking);
  const requestSnapshot = useGame((s) => s.requestSnapshot);
  const ensureConnected = useGame((s) => s.ensureConnected);

  const settled = !loading && wsReady;

  // Navigating during render is a side effect, and React runs render twice in
  // strict mode. An effect keeps it to a single redirect.
  //
  // `exit` has to bypass `settled`. Being kicked, cancelled or displaced closes
  // the socket, which is exactly what makes `wsReady` false — gating the
  // redirect on it would strand the player on "Reconnecting..." forever, and
  // the notice explaining what happened would never be reached.
  useEffect(() => {
    if (exit !== null) {
      navigate("/", { replace: true });
      return;
    }
    // The handshake finished and the server restored no room, so the room this
    // client was in is gone — expired, or closed while it was away. Say so
    // rather than dropping the player on the landing screen with no account of
    // where their room went.
    if (settled && !phase) {
      setExit(RoomExit.Gone);
      navigate("/", { replace: true });
    }
  }, [exit, settled, phase, navigate, setExit]);

  // "Reconnecting" has to actually reconnect. The socket may never have been
  // opened — `connect()` gives up quietly when there is no token yet, which is
  // the normal state at page load — so this keeps trying rather than showing a
  // message about an attempt nobody is making.
  useEffect(() => {
    if (exit !== null || settled) return;
    void ensureConnected();
    const id = setInterval(() => void ensureConnected(), 3_000);
    return () => clearInterval(id);
  }, [exit, settled, ensureConnected]);

  // Asked for once per phase while the waiting state is up, so a client that
  // arrived without a payload repairs itself instead of waiting forever.
  useEffect(() => {
    if (exit !== null || !settled || !phase) return;
    requestSnapshot();
  }, [exit, settled, phase, requestSnapshot]);

  if (exit !== null) return null;

  /*
   * A phase alone is not enough to render. Each screen needs its own payload —
   * the turn, the reveal, the ranking — and every one of them returns null
   * without it, which paints an empty screen and reports nothing.
   *
   * That is reachable in ordinary play. `ResponsesLocked` and `GameFinished`
   * are real phases the server reports, but it sends no turn or ranking for
   * either on reconnect or snapshot, because both last milliseconds during a
   * normal game. Arrive in one — by returning to a room, or reconnecting at the
   * wrong instant — and the client has a phase it cannot draw.
   *
   * So readiness is checked here instead, and a snapshot is requested to repair
   * it, rather than each screen silently rendering nothing.
   */
  const ready =
    phase === GamePhase.Lobby
      ? roomCode !== null
      : phase === GamePhase.DecisionSetup
        ? true
        : phase === GamePhase.TurnActive || phase === GamePhase.ResponsesLocked
          ? turn !== null
          : phase === GamePhase.Reveal
            ? reveal !== null
            : phase === GamePhase.GameFinished ||
                phase === GamePhase.ResultsFinalized
              ? ranking !== null
              : true;

  if (!settled || !phase || !ready) {
    return (
      <div className="mx-auto flex min-h-full max-w-md flex-col items-center justify-center px-5 text-center">
        <p className="text-sm text-ink-dim">{t.connection.reconnecting}</p>
      </div>
    );
  }

  switch (phase) {
    case GamePhase.Lobby:
      return <Lobby />;
    case GamePhase.DecisionSetup:
      return <Setup />;
    // `ResponsesLocked` is the instant between the last answer and the reveal
    // being published; keeping the turn on screen avoids a flash of nothing.
    case GamePhase.TurnActive:
    case GamePhase.ResponsesLocked:
      return <Turn />;
    case GamePhase.Reveal:
      return <Reveal />;
    // `GameFinished` is the beat before the ranking is published; the results
    // screen renders nothing until it arrives, so both phases map here.
    case GamePhase.GameFinished:
    case GamePhase.ResultsFinalized:
      return <Results />;
    default:
      // Setup, turns, reveal and results are the screens still to land. Until
      // then the phase name is at least honest about where the room is.
      return (
        <div className="mx-auto flex min-h-full max-w-md flex-col items-center justify-center px-5 text-center">
          <h1 className="text-2xl">{phase}</h1>
        </div>
      );
  }
};
